<template>
  <div class="contact-selection">
    <div v-if="capReached" class="cap-warning">
      Showing the first {{ totalItems }} prospects — refine the URL for more.
    </div>

    <DataTable
      class="prospects-table"
      :data="visibleContacts"
      :columns="columns"
      :selectable="true"
      :loading="loading"
      :initial-selected-ids="selectedIds"
      :on-selection-change="handleSelectionChange"
      :pagination="paginationInfo"
      :show-pagination-info="true"
      :selected-count="selectedIds.length"
      :empty-state="{
        title: 'No prospects on this page',
        description: 'Try adjusting your search URL or load the next page.',
        icon: 'mdi:account-search-outline',
      }"
    >
      <template #cell(name)="{ row }">
        <div class="contact-cell">
          <div class="avatar">
            <img
              v-if="row.profile_picture_url"
              :src="row.profile_picture_url"
              :alt="row.name"
              class="avatar-img"
              @error="handleImageError"
            />
            <div v-else class="avatar-placeholder">
              {{ getInitials(row.name) }}
            </div>
          </div>
          <div class="contact-info">
            <span class="contact-name">{{ row.name }}</span>
            <span v-if="row.headline" class="contact-headline">{{ row.headline }}</span>
          </div>
        </div>
      </template>
      <template #cell(location)="{ row }">
        <span class="location">{{ row.location || "—" }}</span>
      </template>
      <template #cell(network_distance)="{ row }">
        <Badge
          v-if="row.network_distance"
          :variant="getConnectionVariant(row.network_distance)"
          size="sm"
        >
          {{ formatConnectionDegree(row.network_distance) }}
        </Badge>
        <span v-else>—</span>
      </template>
      <template #cell(profile)="{ row }">
        <a
          v-if="row.profile_url || row.public_profile_url"
          :href="row.profile_url || row.public_profile_url"
          target="_blank"
          rel="noopener noreferrer"
          class="profile-link"
          @click.stop
        >
          <Icon icon="mdi:linkedin" class="linkedin-icon" />
          <span>View Profile</span>
        </a>
        <span v-else>—</span>
      </template>
    </DataTable>

    <div class="pagination">
      <button
        type="button"
        class="page-btn"
        :disabled="!canGoPrev || loading"
        @click="emit('prev-page')"
      >
        <Icon icon="mdi:chevron-left" width="16" height="16" />
        Prev
      </button>
      <span class="page-indicator">
        Page {{ currentPage + 1
        }}<span v-if="estimatedTotalPages"> of {{ estimatedTotalPages }}</span>
      </span>
      <button
        type="button"
        class="page-btn"
        :disabled="!canGoNext || loading"
        @click="emit('next-page')"
      >
        Next
        <Icon icon="mdi:chevron-right" width="16" height="16" />
      </button>
      <div class="per-page">
        <div class="per-page-select">
          <Select
            :model-value="String(perPage)"
            :options="perPageOptions"
            size="sm"
            @update:model-value="onPerPageChange"
          />
        </div>
        <span class="per-page-label">per page</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { Badge, Icon, DataTable, Select } from "@/components/ui";
import type { TableColumn, PaginationState } from "@/components/ui/table/types";
import type { LinkedInSearchProspect } from "@/dtos/Unipile.dto";

interface Props {
  contacts: LinkedInSearchProspect[];
  selectedIds: string[];
  totalCount: number | null;
  totalItems: number;
  currentPage: number;
  perPage: number;
  canGoNext: boolean;
  canGoPrev: boolean;
  loading: boolean;
  capReached: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  "update:selectedIds": [ids: string[]];
  "update:perPage": [perPage: number];
  "prev-page": [];
  "next-page": [];
}>();

const PER_PAGE_OPTIONS = [50, 100, 200];

const perPageOptions = computed(() =>
  PER_PAGE_OPTIONS.map((opt) => ({ value: String(opt), label: String(opt) }))
);

const visibleContacts = computed(() =>
  props.contacts.map((contact) => ({
    ...contact,
    id: contact.id || contact.member_urn || "",
  }))
);

const paginationInfo = computed<PaginationState>(() => ({
  page: props.currentPage + 1,
  perPage: props.perPage,
  total:
    typeof props.totalCount === "number" && props.totalCount > 0
      ? props.totalCount
      : props.totalItems,
}));

const estimatedTotalPages = computed(() => {
  if (typeof props.totalCount !== "number" || props.perPage <= 0) return 0;
  return Math.max(1, Math.ceil(props.totalCount / props.perPage));
});

const columns = computed<TableColumn[]>(() => [
  { key: "name", header: "Contact", width: "35%" },
  { key: "location", header: "Location", width: "25%" },
  { key: "network_distance", header: "Connection", width: "15%" },
  { key: "profile", header: "Profile", width: "20%" },
]);

const handleSelectionChange = (ids: (string | number)[]) => {
  const visibleSet = new Set(visibleContacts.value.map((c) => String(c.id)));
  const incoming = new Set(ids.map(String));
  const next = new Set<string>();
  // Preserve selections from other pages
  for (const id of props.selectedIds) {
    if (!visibleSet.has(id)) next.add(id);
  }
  // Apply current page selections
  for (const id of incoming) next.add(id);
  emit("update:selectedIds", Array.from(next));
};

const onPerPageChange = (val: string | number) => {
  const num = Number(val);
  if (!Number.isFinite(num) || num <= 0) return;
  emit("update:perPage", num);
};

const getInitials = (name: string): string => {
  if (!name) return "?";
  const parts = name.split(" ").filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const formatConnectionDegree = (distance: string): string => {
  const distanceMap: Record<string, string> = {
    FIRST_DEGREE: "1st",
    SECOND_DEGREE: "2nd",
    THIRD_DEGREE: "3rd",
    OUT_OF_NETWORK: "3rd+",
    DISTANCE_1: "1st",
    DISTANCE_2: "2nd",
    DISTANCE_3: "3rd",
    DISTANCE_4: "3rd+",
  };
  if (distanceMap[distance]) return distanceMap[distance];
  const match = distance.match(/(\d+)/);
  if (match) {
    const num = parseInt(match[1], 10);
    if (num === 1) return "1st";
    if (num === 2) return "2nd";
    if (num === 3) return "3rd";
    return `${num}th`;
  }
  return distance;
};

const getConnectionVariant = (distance: string): "primary" | "secondary" | "info" => {
  const variantMap: Record<string, "primary" | "secondary" | "info"> = {
    FIRST_DEGREE: "primary",
    SECOND_DEGREE: "secondary",
    THIRD_DEGREE: "info",
    OUT_OF_NETWORK: "info",
    DISTANCE_1: "primary",
    DISTANCE_2: "secondary",
    DISTANCE_3: "info",
  };
  if (variantMap[distance]) return variantMap[distance];
  const match = distance.match(/(\d+)/);
  if (match) {
    const num = parseInt(match[1], 10);
    if (num === 1) return "primary";
    if (num === 2) return "secondary";
  }
  return "info";
};

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  img.style.display = "none";
  const parent = img.parentElement;
  if (parent) parent.classList.add("avatar-fallback");
};
</script>

<style scoped>
.contact-selection {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
}

.cap-warning {
  font-size: 0.75rem;
  color: #b45309;
  padding: 0 4px;
}

.prospects-table :deep(.data-table__cell) {
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}

.contact-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  background-color: #f3f4f6;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder,
.avatar-fallback::after {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 12px;
  font-weight: 600;
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
}

.contact-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-headline {
  font-size: 0.75rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.location {
  font-size: 0.875rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.profile-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  color: #0077b5;
  text-decoration: none;
  transition: color 0.2s ease;
}

.profile-link:hover {
  color: #005885;
  text-decoration: underline;
}

.linkedin-icon {
  font-size: 16px;
}

.pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  column-gap: 16px;
  row-gap: 10px;
  padding: 10px 4px 4px;
  position: sticky;
  bottom: 0;
  background: #ffffff;
  z-index: 10;
  border-top: 1px solid #f3f4f6;
  margin-top: 4px;
}

.page-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: 1px solid #d0d5dd;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  color: #374151;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background-color: #f6f4ff;
  border-color: #7d7df7;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-indicator {
  font-size: 12px;
  color: #425166;
  white-space: nowrap;
}

.per-page {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.per-page-select {
  width: 70px;
  flex-shrink: 0;
}

.per-page-select :deep(.ui-select),
.per-page-select :deep(.ui-select__field) {
  width: 100%;
  min-width: 0;
}

.per-page-label {
  font-size: 12px;
  color: #425166;
  white-space: nowrap;
  flex-shrink: 0;
}
</style>
